import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import { formatDate, getStatementDueDate } from '../lib/dateUtils';
import { buildStatementTemplateValues, formatStatementCurrency, renderStatementTemplate } from '../lib/statementTemplate';

const ACCENT = '#111827';

const styles = StyleSheet.create({
  page: {
    paddingTop: 50,
    paddingBottom: 60,
    paddingHorizontal: 48,
    fontSize: 9.5,
    fontFamily: 'Helvetica',
    color: '#1f2937',
  },
  accentBar: {
    height: 4,
    backgroundColor: ACCENT,
    marginBottom: 24,
  },

  // ---------- Letterhead ----------
  letterheadRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 28,
  },
  letterheadLeft: {
    flexDirection: 'column',
    maxWidth: '55%',
  },
  logo: {
    height: 42,
    marginBottom: 8,
    objectFit: 'contain',
  },
  bcName: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 3,
  },
  bcDetail: {
    fontSize: 8.5,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  letterheadRight: {
    alignItems: 'flex-end',
  },
  statementTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: ACCENT,
    letterSpacing: 1,
    marginBottom: 8,
  },
  metaRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  metaLabel: {
    fontSize: 8.5,
    color: '#6b7280',
    width: 80,
    textAlign: 'right',
    marginRight: 8,
  },
  metaValue: {
    fontSize: 8.5,
    fontWeight: 'bold',
    color: '#111827',
    textAlign: 'right',
  },

  // ---------- Owner / account panel ----------
  infoPanel: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    borderRadius: 6,
    padding: 14,
    marginBottom: 22,
  },
  infoColumn: {
    flex: 1,
  },
  infoHeading: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#9ca3af',
    letterSpacing: 0.5,
    marginBottom: 5,
  },
  infoName: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 2,
  },
  infoLine: {
    fontSize: 9,
    color: '#4b5563',
    marginBottom: 1,
  },

  // ---------- Section titles ----------
  sectionTitle: {
    fontSize: 9.5,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1.5,
    borderBottomColor: '#111827',
  },
  section: {
    marginBottom: 22,
  },
  introduction: {
    marginBottom: 20,
    paddingLeft: 12,
    borderLeftWidth: 3,
    borderLeftColor: ACCENT,
    fontSize: 9,
    color: '#4b5563',
    lineHeight: 1.5,
  },

  // ---------- Summary ----------
  summaryTable: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 4,
  },
  summaryRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  summaryRowLabel: {
    flex: 1,
    fontSize: 9,
    color: '#4b5563',
  },
  summaryRowValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#111827',
    width: 90,
    textAlign: 'right',
  },
  amountDueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#111827',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    marginTop: -1,
  },
  amountDueLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
    letterSpacing: 0.5,
  },
  amountDueValue: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#ffffff',
  },

  // ---------- Transaction table ----------
  table: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#111827',
    paddingVertical: 7,
    paddingHorizontal: 8,
  },
  tableHeaderText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#ffffff',
    letterSpacing: 0.3,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  tableRowAlt: {
    backgroundColor: '#f9fafb',
  },
  tableCellDate: { width: '13%', fontSize: 8.5 },
  tableCellDesc: { width: '31%', fontSize: 8.5 },
  tableCellRef: { width: '17%', fontSize: 8.5, color: '#6b7280' },
  tableCellDebit: { width: '13%', fontSize: 8.5, textAlign: 'right', color: '#b91c1c' },
  tableCellCredit: { width: '13%', fontSize: 8.5, textAlign: 'right', color: '#15803d' },
  tableCellBalance: { width: '13%', fontSize: 8.5, textAlign: 'right', fontWeight: 'bold' },
  closingBalanceRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 8,
    backgroundColor: '#f3f4f6',
  },
  closingBalanceLabel: {
    // Span every column before Balance so the value aligns with the Balance column.
    width: '87%',
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'right',
    paddingRight: 12,
  },
  closingBalanceValue: {
    width: '13%',
  },

  // ---------- Payment details ----------
  paymentPanel: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 6,
    padding: 14,
  },
  paymentGrid: {
    flexDirection: 'row',
  },
  paymentColumn: {
    flex: 1,
  },
  paymentRow: {
    marginBottom: 6,
  },
  paymentLabel: {
    fontSize: 7.5,
    color: '#9ca3af',
    marginBottom: 1,
  },
  paymentValue: {
    fontSize: 9.5,
    fontWeight: 'bold',
    color: '#111827',
  },
  paymentAmountDue: {
    color: '#b91c1c',
    fontSize: 11,
  },

  // ---------- Notice / footer ----------
  notice: {
    marginTop: 22,
    padding: 10,
    backgroundColor: '#fffbeb',
    borderWidth: 1,
    borderColor: '#fde68a',
    borderRadius: 4,
    fontSize: 8,
    color: '#92400e',
    lineHeight: 1.4,
  },
  terms: {
    marginTop: 16,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    fontSize: 8,
    color: '#4b5563',
    lineHeight: 1.4,
  },
  termsHeading: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 4,
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 7.5,
    color: '#9ca3af',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 8,
  },
});

interface StatementPDFProps {
  data: {
    account: any;
    transactions: any[];
    summary: any;
    periodFrom: string;
    periodTo: string;
    generatedDate: string;
    bodyCorporate: any;
    bankAccount: any;
    statementSettings: any;
  };
}

const StatementPDF: React.FC<StatementPDFProps> = ({ data }) => {
  const {
    account,
    transactions,
    summary,
    periodFrom,
    periodTo,
    generatedDate,
    bodyCorporate,
    bankAccount,
    statementSettings,
  } = data;

  const owner = account.owners?.[0] || null;

  const formatCurrency = formatStatementCurrency;

  const closingBalance = transactions.length > 0
    ? transactions[transactions.length - 1].running_balance
    : summary.opening_balance;

  const dueDate = getStatementDueDate(transactions || []);
  const isCredit = summary.amount_due < 0;
  // The old blue value was the application default, not a deliberate scheme colour.
  const primaryColor = !bodyCorporate?.primary_color || bodyCorporate.primary_color === '#2563eb'
    ? ACCENT
    : bodyCorporate.primary_color;
  const footerText = statementSettings?.footer_text
    || `${bodyCorporate?.name || 'Body Corporate'} • Generated ${formatDate(generatedDate)}`;
  const templateValues = buildStatementTemplateValues(data);
  const renderTemplate = (text: string | null | undefined) => renderStatementTemplate(text, templateValues);
  const compactLayout = statementSettings?.layout_style === 'compact';

  const summaryLines = [
    { label: 'Opening Balance', value: summary.opening_balance },
    { label: 'Levies', value: summary.total_levies },
    { label: 'Special Levies', value: summary.total_special_levies },
    { label: 'Other Charges', value: summary.total_other_charges },
    { label: 'Penalties / Interest', value: summary.total_penalties },
    { label: 'Payments Received', value: -summary.total_payments },
    { label: 'Credits / Adjustments', value: -summary.total_credits },
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={{ ...styles.accentBar, backgroundColor: primaryColor }} fixed />

        {/* Letterhead */}
        <View style={styles.letterheadRow}>
          <View style={styles.letterheadLeft}>
            {bodyCorporate?.logo_path && (
              <Image src={bodyCorporate.logo_path} style={styles.logo} />
            )}
            <Text style={styles.bcName}>{bodyCorporate?.name || 'Body Corporate'}</Text>
            <Text style={styles.bcDetail}>{bodyCorporate?.physical_address || ''}</Text>
            <Text style={styles.bcDetail}>
              {bodyCorporate?.telephone || ''}
              {bodyCorporate?.telephone && bodyCorporate?.email ? '  •  ' : ''}
              {bodyCorporate?.email || ''}
            </Text>
            {bodyCorporate?.registration_number && (
              <Text style={styles.bcDetail}>Reg No: {bodyCorporate.registration_number}</Text>
            )}
          </View>

          <View style={styles.letterheadRight}>
            <Text style={{ ...styles.statementTitle, color: primaryColor }}>
              {(statementSettings?.statement_title || 'LEVY STATEMENT').toUpperCase()}
            </Text>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Statement Date</Text>
              <Text style={styles.metaValue}>{formatDate(generatedDate)}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Period</Text>
              <Text style={styles.metaValue}>{formatDate(periodFrom)} – {formatDate(periodTo)}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Account No.</Text>
              <Text style={styles.metaValue}>{account.account_number}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Unit</Text>
              <Text style={styles.metaValue}>{account.flat?.flat_number || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* Owner panel */}
        <View style={styles.infoPanel}>
          <View style={styles.infoColumn}>
            <Text style={styles.infoHeading}>STATEMENT FOR</Text>
            <Text style={styles.infoName}>{owner?.full_name || 'N/A'}</Text>
            <Text style={styles.infoLine}>{owner?.email || ''}</Text>
            <Text style={styles.infoLine}>{owner?.telephone || ''}</Text>
          </View>
          <View style={styles.infoColumn}>
            <Text style={styles.infoHeading}>PROPERTY</Text>
            <Text style={styles.infoName}>Unit {account.flat?.flat_number || 'N/A'}</Text>
            <Text style={styles.infoLine}>{account.flat?.physical_address || ''}</Text>
            {account.flat?.participation_quota != null && (
              <Text style={styles.infoLine}>Participation quota: {account.flat.participation_quota}%</Text>
            )}
          </View>
        </View>

        {statementSettings?.intro_text && (
          <View style={{ ...styles.introduction, borderLeftColor: primaryColor }}>
            <Text>{renderTemplate(statementSettings.intro_text)}</Text>
          </View>
        )}

        {/* Account Summary */}
        <View style={{ ...styles.section, marginBottom: compactLayout ? 16 : 22 }}>
          <Text style={{ ...styles.sectionTitle, borderBottomColor: primaryColor }}>
            {renderTemplate(statementSettings?.summary_heading || 'Account summary').toUpperCase()}
          </Text>
          <View style={styles.summaryTable}>
            {summaryLines.map((line, i) => (
              <View
                key={line.label}
                style={i === summaryLines.length - 1
                  ? { ...styles.summaryRow, borderBottomWidth: 0 }
                  : styles.summaryRow}
              >
                <Text style={styles.summaryRowLabel}>{line.label}</Text>
                <Text
                  style={{
                    ...styles.summaryRowValue,
                    color: line.value < 0 ? '#15803d' : '#111827',
                  }}
                >
                  {formatCurrency(line.value)}
                </Text>
              </View>
            ))}
          </View>
          <View style={{ ...styles.amountDueRow, backgroundColor: primaryColor }}>
            <Text style={styles.amountDueLabel}>
              {isCredit ? 'ACCOUNT IN CREDIT' : 'TOTAL AMOUNT DUE'}
            </Text>
            <Text style={styles.amountDueValue}>{formatCurrency(Math.abs(summary.amount_due))}</Text>
          </View>
        </View>

        {/* Transaction Table */}
        <View style={{ ...styles.section, marginBottom: compactLayout ? 16 : 22 }}>
          <Text style={{ ...styles.sectionTitle, borderBottomColor: primaryColor }}>
            {renderTemplate(statementSettings?.transactions_heading || 'Transaction history').toUpperCase()}
          </Text>
          <View style={styles.table}>
            <View style={{ ...styles.tableHeader, backgroundColor: primaryColor }} fixed>
              <Text style={{ ...styles.tableHeaderText, width: '13%' }}>Date</Text>
              <Text style={{ ...styles.tableHeaderText, width: '31%' }}>Description</Text>
              <Text style={{ ...styles.tableHeaderText, width: '17%' }}>Reference</Text>
              <Text style={{ ...styles.tableHeaderText, width: '13%', textAlign: 'right' }}>Debit</Text>
              <Text style={{ ...styles.tableHeaderText, width: '13%', textAlign: 'right' }}>Credit</Text>
              <Text style={{ ...styles.tableHeaderText, width: '13%', textAlign: 'right' }}>Balance</Text>
            </View>

            {transactions.length === 0 && (
              <View style={styles.tableRow}>
                <Text style={{ fontSize: 8.5, color: '#9ca3af' }}>
                  No transactions were posted during this period.
                </Text>
              </View>
            )}

            {transactions.map((t: any, idx: number) => (
              <View
                key={t.id}
                wrap={false}
                style={idx % 2 === 1
                  ? { ...styles.tableRow, ...styles.tableRowAlt }
                  : styles.tableRow}
              >
                <Text style={styles.tableCellDate}>{formatDate(t.transaction_date)}</Text>
                <Text style={styles.tableCellDesc}>{t.description}</Text>
                <Text style={styles.tableCellRef}>{t.reference_number || '-'}</Text>
                <Text style={styles.tableCellDebit}>
                  {t.debit_amount > 0 ? formatCurrency(t.debit_amount) : '-'}
                </Text>
                <Text style={styles.tableCellCredit}>
                  {t.credit_amount > 0 ? formatCurrency(t.credit_amount) : '-'}
                </Text>
                <Text style={styles.tableCellBalance}>
                  {formatCurrency(t.running_balance)}
                </Text>
              </View>
            ))}

            <View style={styles.closingBalanceRow} wrap={false}>
              <Text style={styles.closingBalanceLabel}>CLOSING BALANCE</Text>
              <View style={styles.closingBalanceValue}>
                <Text style={{ fontSize: 9.5, fontWeight: 'bold', textAlign: 'right' }}>
                  {formatCurrency(closingBalance)}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Payment Details */}
        <View style={styles.paymentPanel} wrap={false}>
          <Text style={{ fontSize: 9.5, fontWeight: 'bold', marginBottom: 10 }}>
            {renderTemplate(statementSettings?.payment_heading || 'Payment details').toUpperCase()}
          </Text>
          <View style={styles.paymentGrid}>
            <View style={styles.paymentColumn}>
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>BANK</Text>
                <Text style={styles.paymentValue}>{bankAccount?.bank_name || 'N/A'}</Text>
              </View>
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>ACCOUNT NAME</Text>
                <Text style={styles.paymentValue}>{bankAccount?.account_name || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.paymentColumn}>
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>ACCOUNT NUMBER</Text>
                <Text style={styles.paymentValue}>{bankAccount?.account_number || 'N/A'}</Text>
              </View>
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>BRANCH CODE</Text>
                <Text style={styles.paymentValue}>{bankAccount?.branch_code || 'N/A'}</Text>
              </View>
            </View>
            <View style={styles.paymentColumn}>
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>PAYMENT REFERENCE</Text>
                <Text style={styles.paymentValue}>{account.account_number}</Text>
              </View>
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>DUE DATE</Text>
                <Text style={styles.paymentValue}>{formatDate(dueDate || periodTo)}</Text>
              </View>
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>AMOUNT DUE</Text>
                <Text style={{ ...styles.paymentValue, ...styles.paymentAmountDue }}>
                  {formatCurrency(Math.abs(summary.amount_due))}
                </Text>
              </View>
            </View>
          </View>
          {statementSettings?.payment_instructions && (
            <Text style={{ marginTop: 8, fontSize: 8, color: '#4b5563', lineHeight: 1.4 }}>
              {renderTemplate(statementSettings.payment_instructions)}
            </Text>
          )}
        </View>

        {/* Notice */}
        {statementSettings?.notice_text && (
          <View style={styles.notice} wrap={false}>
            <Text>{renderTemplate(statementSettings.notice_text)}</Text>
          </View>
        )}

        {/* Terms and conditions */}
        {statementSettings?.terms_text && (
          <View style={styles.terms}>
            <Text style={styles.termsHeading}>TERMS &amp; CONDITIONS</Text>
            <Text>{renderTemplate(statementSettings.terms_text)}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text>{renderTemplate(footerText)}</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default StatementPDF;
